/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { Button as AriaButton } from 'react-aria-components/Button';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { IconProvider } from '../icon/context';
import { ButtonContext } from './context';
import styles from './styles.module.css';
import type { ButtonProps } from './types';

/**
 * Button - A versatile interactive button component with multiple variants
 *
 * Provides accessible button functionality with support for different visual styles,
 * sizes, and interactive states. Includes icon support and integrates with React Aria
 * for keyboard navigation and accessibility features.
 *
 * @param props - The button props.
 * @param props.ref - Reference to the button element.
 * @param props.children - Button content (text, icons, or both).
 * @param props.className - Additional CSS class names for styling.
 * @param props.color - Semantic color variant.
 * @param props.size - Size of the button.
 * @param props.variant - Visual style variant.
 * @returns The button component.
 *
 * @example
 * ```tsx
 * // Basic button
 * <Button>Click me</Button>
 * ```
 *
 * @example
 * ```tsx
 * // Primary button with different sizes
 * <Button variant="filled" size="large">Large Filled</Button>
 * <Button variant="outline" size="small">Small Outline</Button>
 * ```
 *
 * @example
 * ```tsx
 * // Button with icon
 * <Button variant="flat">
 *   <Icon><Plus /></Icon>
 *   Add Item
 * </Button>
 * ```
 *
 * @example
 * ```tsx
 * // Icon-only button
 * <Button variant="icon">
 *   <Icon><Settings /></Icon>
 * </Button>
 * ```
 *
 * @example
 * ```tsx
 * // Button with different colors
 * <Button color="critical">Critical Button</Button>
 * <Button color="serious">Delete</Button>
 * ```
 */
export function Button({ ref, ...props }: ButtonProps) {
  [props, ref] = useContextProps(props, ref ?? null, ButtonContext);

  const {
    children,
    className,
    color = 'mono-muted',
    size = 'medium',
    variant = 'filled',
    ...rest
  } = props;

  return (
    <IconProvider size={size}>
      <AriaButton
        {...rest}
        ref={ref}
        className={composeRenderProps(className, (className) =>
          clsx('group/button', styles.button, styles[variant], className),
        )}
        data-color={color}
        data-size={size}
      >
        {children}
      </AriaButton>
    </IconProvider>
  );
}
